<script setup>
/* global defineProps */
import { computed } from "vue";

const props = defineProps({
  data: { type: Array, default: () => [] },
  maxValue: { type: Number, default: null },
  goalLine: { type: Number, default: null },
  mode: { type: String, default: "bars" },
});

const chartMax = computed(() => {
  const dataMax = Math.max(0, ...props.data.map((item) => Number(item.value) || 0));
  return Math.max(1, props.maxValue || 0, props.goalLine || 0, dataMax);
});

const labelInterval = computed(() => {
  if (props.data.length <= 7) return 1;
  if (props.data.length <= 30) return 5;
  return 30;
});

const chartMinWidth = computed(() => {
  if (props.data.length <= 7) return "420px";
  if (props.data.length <= 30) return "620px";
  return "900px";
});

const columnMinWidth = computed(() => {
  if (props.data.length <= 7) return "36px";
  if (props.data.length <= 30) return "14px";
  return "3px";
});

const columnGap = computed(() => (props.data.length > 30 ? "2px" : "6px"));

const heightFor = (value) => {
  const percent = ((Number(value) || 0) / chartMax.value) * 100;
  return `${Math.max(value > 0 ? 5 : 0, Math.min(100, percent))}%`;
};

const showLabel = (index) =>
  index === 0 ||
  index === props.data.length - 1 ||
  (index + 1) % labelInterval.value === 0;
</script>

<template>
  <div class="chart-shell">
    <div
      class="chart"
      :class="{ 'activity-chart': mode === 'activity' }"
      :style="{
        gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(${columnMinWidth}, 1fr))`,
        gap: columnGap,
        minWidth: chartMinWidth,
      }"
    >
      <div
        v-if="goalLine != null"
        class="goal-line"
        :style="{ bottom: `${Math.min(100, (goalLine / chartMax) * 100)}%` }"
      >
        <span>Goal {{ goalLine }}</span>
      </div>
      <div
        v-for="(item, index) in data"
        :key="item.date || index"
        class="bar-column"
        :title="`${item.label}: ${item.value}`"
      >
        <div class="bar-stage">
          <div
            class="bar"
            :class="{ active: mode === 'activity' && item.value > 0 }"
            :style="{ height: heightFor(item.value) }"
          ></div>
        </div>
        <span class="axis-label">{{ showLabel(index) ? item.label : "" }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-shell {
  width: 100%;
  overflow-x: auto;
  padding-top: 18px;
}

.chart {
  position: relative;
  display: grid;
  height: 230px;
  border-bottom: 1px solid var(--hover-bg);
}

.bar-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.bar-stage {
  height: 190px;
  width: 100%;
  display: flex;
  align-items: flex-end;
}

.bar {
  width: 100%;
  min-height: 0;
  border-radius: 4px 4px 1px 1px;
  background: var(--accent);
  opacity: 0.78;
  transition: opacity 120ms ease, transform 120ms ease;
}

.bar-column:hover .bar {
  opacity: 1;
  transform: translateY(-2px);
}

.activity-chart .bar {
  background: var(--hover-bg);
  opacity: 1;
}

.activity-chart .bar.active {
  background: #44d281;
}

.axis-label {
  height: 28px;
  margin-top: 8px;
  color: var(--text-color);
  font-size: 0.68rem;
  white-space: nowrap;
}

.goal-line {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  border-top: 1px dashed #d28a44;
  pointer-events: none;
}

.goal-line span {
  position: absolute;
  right: 0;
  bottom: 3px;
  padding: 2px 5px;
  border-radius: 4px;
  background: var(--sidebar-bg);
  color: #d28a44;
  font-size: 0.68rem;
}

</style>
